import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

interface TypewriterBackButtonProps {
  label: string;
  to: string;
  className?: string;
  /** ms per character while typing forward. Default 14. */
  typeSpeedMs?: number;
  /** ms per character while backspacing. Default 10. */
  eraseSpeedMs?: number;
  /** Runs before navigating; `event.preventDefault()` stays on the page. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Square arrow icon that, on hover/focus, types out a label letter-by-letter
 * with a soft brand-purple highlight, and backspaces back to icon-only when
 * the pointer leaves. Honours prefers-reduced-motion (full text instantly,
 * no caret blink).
 */
export function TypewriterBackButton({
  label,
  to,
  className,
  typeSpeedMs = 14,
  eraseSpeedMs = 10,
  onClick,
}: TypewriterBackButtonProps) {
  const [text, setText] = useState("");
  const [hovered, setHovered] = useState(false);
  const reducedRef = useRef(false);

  useEffect(() => {
    reducedRef.current =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    if (reducedRef.current) {
      setText(hovered ? label : "");
      return;
    }
    if (hovered) {
      if (text.length === label.length) return;
      const id = window.setTimeout(
        () => setText(label.slice(0, text.length + 1)),
        typeSpeedMs,
      );
      return () => window.clearTimeout(id);
    }
    if (text.length === 0) return;
    const id = window.setTimeout(
      () => setText(label.slice(0, text.length - 1)),
      eraseSpeedMs,
    );
    return () => window.clearTimeout(id);
  }, [hovered, text, label, typeSpeedMs, eraseSpeedMs]);

  const isOpen = text.length > 0;
  const isTyping = hovered && text.length < label.length;

  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      aria-label={label}
      className={cn(
        "group inline-flex h-8 items-center gap-1.5 rounded-md text-xs font-medium",
        "transition-[background-color,color,padding] duration-200 ease-out motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        isOpen
          ? "bg-threadheads-purple/10 text-threadheads-purple px-2.5"
          : "px-1.5 text-muted-foreground hover:text-threadheads-purple",
        className,
      )}
    >
      <ArrowLeft
        className={cn(
          "h-3.5 w-3.5 shrink-0 transition-transform duration-200 motion-reduce:transition-none",
          hovered && "-translate-x-0.5",
        )}
        aria-hidden="true"
      />
      <span aria-hidden="true" className="overflow-hidden whitespace-nowrap font-mono tracking-tight">
        {text}
        {/* Blinking caret while typing forward — fades out once the line is fully typed. */}
        {isTyping && (
          <span className="ml-px inline-block w-[1px] -mb-px bg-threadheads-purple motion-safe:animate-pulse">
            &nbsp;
          </span>
        )}
      </span>
    </Link>
  );
}
